import { useCallback, useId, useMemo, useState, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  SelectionMode,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  type Node,
  type Edge,
  type Connection,
  type NodeTypes,
  type EdgeTypes,
  type OnConnect,
  type NodeChange,
  type ReactFlowInstance,
  Handle,
  Position,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { PipelineGraphDto, PipelineNodeDto, PipelineEdgeDto, ConditionGroup, ConditionRule, DecisionOperator, ConditionLogic } from '../../types';
import { ProcessingStepType } from '../../types';
import type { FieldSuggestion, NormalizationConfig, EnrichmentConfig } from '../../types/pipelineConfig';
import { getFieldSuggestions, getNormalizedFieldSuggestions } from '../../utils/pipelineDefaults';
import { StepConfigPanel } from './StepConfigPanel';
import { useEscapeKey } from '../../hooks/useEscapeKey';

// --- Types ---

interface PipelineFlowEditorProps {
  graph: PipelineGraphDto;
  onSave: (nodes: PipelineNodeDto[], edges: PipelineEdgeDto[]) => Promise<void>;
  endpointType?: string | number;
  tenantId?: string;
  customerId?: string;
}

interface StepNodeData {
  [key: string]: unknown;
  label: string;
  stepType: string | null;
  isEnabled: boolean;
  configuration: string | null;
  nodeType: 'Step';
  icon: string;
  branchSubtitle?: string;
}

interface DecisionNodeData {
  [key: string]: unknown;
  label: string;
  conditionField: string;
  conditionOperator: string;
  branchSubtitle?: string;
  nodeType: 'Decision';
}

interface MergeNodeData {
  [key: string]: unknown;
  label: string;
  nodeType: 'Merge';
}

type FlowNodeData = StepNodeData | DecisionNodeData | MergeNodeData;

function getStepEnabledState(isEnabled: boolean | undefined, configuration: string | null | undefined): boolean {
  if (typeof isEnabled === 'boolean') {
    return isEnabled;
  }

  if (!configuration) {
    return true;
  }

  try {
    const parsedConfig = JSON.parse(configuration) as { enabled?: boolean; isEnabled?: boolean };

    if (typeof parsedConfig.isEnabled === 'boolean') {
      return parsedConfig.isEnabled;
    }

    if (typeof parsedConfig.enabled === 'boolean') {
      return parsedConfig.enabled;
    }
  } catch {
    // Ignore malformed config payloads and fall back to enabled by default.
  }

  return true;
}

// --- Constants ---

const stepIcons: Record<string, string> = {
  Validation: '✓',
  Normalization: '🔄',
  Deduplication: '🔗',
  Suppression: '🔇',
  Enrichment: '📊',
  Correlation: '🔍',
  IncidentGate: '⏳',
  IncidentCreation: '🎫',
  Notification: '📧',
  Initialization: '🚀',
  Custom: '⚙️',
};

const STEP_TYPES = [
  'Validation', 'Normalization', 'Deduplication', 'Suppression',
  'Enrichment', 'Correlation', 'IncidentGate', 'IncidentCreation', 'Notification',
] as const;

const CONDITION_OPERATORS: Array<{ value: DecisionOperator; label: string }> = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Not Contains' },
  { value: 'startsWith', label: 'Starts With' },
  { value: 'endsWith', label: 'Ends With' },
  { value: 'matches', label: 'Regex Match' },
  { value: 'in', label: 'In List' },
  { value: 'notIn', label: 'Not In List' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'gte', label: 'Greater Than or Equal' },
  { value: 'lt', label: 'Less Than' },
  { value: 'lte', label: 'Less Than or Equal' },
  { value: 'between', label: 'Between' },
  { value: 'exists', label: 'Exists (not null)' },
  { value: 'notExists', label: 'Not Exists (null)' },
];

/**
 * Get available condition fields for a decision node based on its position in the DAG.
 * - Before Normalization: show raw ingestion fields only
 * - After Normalization: show normalized fields (standard + custom from config)
 * - After Enrichment: also show enriched fields (from config)
 */
function getAvailableConditionFields(
  nodeId: string,
  nodes: Node[],
  edges: Edge[],
  endpointType: string | number,
): FieldSuggestion[] {
  const visited = new Set<string>();
  let hasNormalizationUpstream = false;
  let hasEnrichmentUpstream = false;
  const customNormalizedFields: FieldSuggestion[] = [];
  const customEnrichedFields: FieldSuggestion[] = [];

  // BFS backwards through edges
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);

    const incomingEdges = edges.filter(e => e.target === current);
    for (const edge of incomingEdges) {
      const sourceNode = nodes.find(n => n.id === edge.source);
      if (!sourceNode) continue;

      const nodeData = sourceNode.data as Record<string, unknown>;
      const stepType = nodeData.stepType as string | null;
      const config = nodeData.configuration as string | null;

      if (stepType === ProcessingStepType.Normalization) {
        hasNormalizationUpstream = true;
        if (config) {
          try {
            const normConfig = JSON.parse(config) as NormalizationConfig;
            const mappings = normConfig.defaultNormalization?.rules || [];
            for (const rule of mappings) {
              if (rule.targetField && !customNormalizedFields.some(f => f.path === `normalized.${rule.targetField}`)) {
                customNormalizedFields.push({
                  path: `normalized.${rule.targetField}`,
                  label: `normalized.${rule.targetField}`,
                  description: `Mapped from ${rule.sourceField}`,
                  category: 'Normalized Fields (Custom)',
                });
              }
            }
          } catch { /* ignore */ }
        }
      }

      if (stepType === ProcessingStepType.Enrichment) {
        hasEnrichmentUpstream = true;
        if (config) {
          try {
            const enrichConfig = JSON.parse(config) as EnrichmentConfig;
            const rules = enrichConfig.rules || [];
            for (const rule of rules) {
              if (rule.targetField && !customEnrichedFields.some(f => f.path === rule.targetField)) {
                customEnrichedFields.push({
                  path: rule.targetField,
                  label: rule.targetField,
                  description: `Enriched by ${rule.name || rule.sourceType}`,
                  category: 'Enriched Fields (Custom)',
                });
              }
            }
          } catch { /* ignore */ }
        }
      }

      queue.push(edge.source);
    }
  }

  const fields: FieldSuggestion[] = [];

  if (!hasNormalizationUpstream) {
    // Before normalization: raw ingestion fields
    fields.push(...getFieldSuggestions(endpointType, 'normalization'));
  } else {
    // After normalization: normalized fields
    fields.push(...getNormalizedFieldSuggestions());
    fields.push(...customNormalizedFields.filter(f => !fields.some(e => e.path === f.path)));
  }

  if (hasEnrichmentUpstream) {
    fields.push(...customEnrichedFields.filter(f => !fields.some(e => e.path === f.path)));
  }

  return fields;
}

// --- Custom Nodes ---

function StepNodeComponent({ data, selected }: { data: StepNodeData; selected?: boolean }) {
  const isEnabled = getStepEnabledState(data.isEnabled, data.configuration);

  return (
    <div
      className={`
        px-4 py-3 rounded-xl border-2 min-w-[200px] max-w-[280px] transition-shadow cursor-pointer
        ${isEnabled
          ? 'border-green-300 dark:border-green-700 bg-white dark:bg-gray-800 shadow-sm'
          : 'border-gray-400 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 opacity-60'
        }
        ${selected ? 'ring-2 ring-primary-700 shadow-md' : ''}
      `}
      title="Double-click to configure"
    >
      <Handle type="target" position={Position.Top} className="!bg-primary-700 !w-3 !h-3 !border-2 !border-white dark:!border-gray-900" />
      <div className="flex items-center gap-2">
        <div className={`
          w-8 h-8 rounded-full flex items-center justify-center text-base shrink-0
          ${isEnabled ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-200 dark:bg-gray-700'}
        `}>
          {data.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium text-gray-900 dark:text-gray-100 truncate ${!isEnabled ? 'line-through' : ''}`}>{data.label}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {data.branchSubtitle || data.stepType || 'Step'}
          </p>
        </div>
        {!isEnabled && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
            Disabled
          </span>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-primary-700 !w-3 !h-3 !border-2 !border-white dark:!border-gray-900" />
    </div>
  );
}

function DecisionNodeComponent({ data, selected }: { data: DecisionNodeData; selected?: boolean }) {
  return (
    <div
      className={`
        px-4 py-3 rounded-xl border-2 border-amber-400 dark:border-amber-600
        bg-amber-50 dark:bg-amber-900/20 shadow-sm min-w-[200px] max-w-[280px] transition-shadow cursor-pointer
        ${selected ? 'ring-2 ring-amber-500 shadow-md' : ''}
      `}
      title="Double-click to configure"
    >
      <Handle type="target" position={Position.Top} className="!bg-amber-500 !w-3 !h-3 !border-2 !border-white dark:!border-gray-900" />
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-base shrink-0">
          ◆
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{data.label}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {data.branchSubtitle || 'Decision'}
          </p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-amber-500 !w-3 !h-3 !border-2 !border-white dark:!border-gray-900" />
    </div>
  );
}

function MergeNodeComponent({ data, selected }: { data: MergeNodeData; selected?: boolean }) {
  return (
    <div
      className={`
        px-4 py-2 rounded-xl border-2 border-blue-300 dark:border-blue-700
        bg-blue-50 dark:bg-blue-900/20 shadow-sm min-w-[140px] transition-shadow
        ${selected ? 'ring-2 ring-blue-500 shadow-md' : ''}
      `}
    >
      <Handle type="target" position={Position.Top} className="!bg-blue-500 !w-3 !h-3 !border-2 !border-white dark:!border-gray-900" />
      <div className="flex items-center gap-2 justify-center">
        <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-sm shrink-0">
          ▼
        </div>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.label}</p>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-blue-500 !w-3 !h-3 !border-2 !border-white dark:!border-gray-900" />
    </div>
  );
}

// --- Custom Edge with Label ---

function ConditionalEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: {
  id: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
  data?: { label?: string; conditionValue?: string; isDefault?: boolean; sortOrder?: number; showLabel?: boolean };
  selected?: boolean;
}) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition,
  });

  const label = data?.label || data?.conditionValue;
  const isDefault = data?.isDefault;
  const shouldShowLabel = data?.showLabel ?? false;
  // Show a fallback label for unconfigured branches so edges are distinguishable
  const displayLabel = isDefault ? '(default)' : label || (data?.sortOrder != null ? `Branch ${data.sortOrder + 1}` : '');

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: selected ? '#6366f1' : isDefault ? '#9ca3af' : '#f59e0b',
          strokeWidth: selected ? 3 : 2,
          strokeDasharray: isDefault ? '5 5' : undefined,
        }}
        markerEnd={`url(#${isDefault ? 'arrow-gray' : 'arrow-amber'})`}
      />
      {shouldShowLabel && displayLabel && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan absolute px-2 py-0.5 rounded text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm pointer-events-auto cursor-pointer"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
            title="Click edge to edit condition"
          >
            {displayLabel}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

// --- Decision Node Editor Modal ---

// Helper: create an empty condition rule
function createEmptyRule(): ConditionRule {
  return { field: '', operator: 'equals', value: '' };
}

// Helper: create an empty condition group
function createEmptyGroup(logic: ConditionLogic = 'AND'): ConditionGroup {
  return { logic, conditions: [createEmptyRule()], groups: [] };
}

// Helper: parse edge conditionValue into a ConditionGroup
function parseEdgeConditionGroup(conditionValue: string | undefined): ConditionGroup {
  if (!conditionValue) return createEmptyGroup();
  const trimmed = conditionValue.trim();
  if (!trimmed.startsWith('{')) {
    // Legacy: single value → wrap in a rule using the node's field/operator
    return createEmptyGroup();
  }
  try {
    const parsed = JSON.parse(trimmed) as ConditionGroup;
    if (parsed.conditions || parsed.groups) return parsed;
    return createEmptyGroup();
  } catch {
    return createEmptyGroup();
  }
}

// --- Condition Rule Editor (single row) ---
function ConditionRuleEditor({
  rule,
  fieldSuggestions,
  onChange,
  onRemove,
  canRemove,
}: {
  rule: ConditionRule;
  fieldSuggestions: FieldSuggestion[];
  onChange: (updated: ConditionRule) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const datalistId = useId();
  const needsValue = !['exists', 'notExists'].includes(rule.operator);
  const needsValue2 = rule.operator === 'between';

  return (
    <div className="flex items-start gap-1.5 group">
      {/* Field */}
      <div className="flex-1 min-w-0">
        <input
          type="text"
          list={datalistId}
          value={rule.field}
          onChange={e => onChange({ ...rule, field: e.target.value })}
          placeholder="Field path"
          className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
          aria-label="Condition field"
        />
        <datalist id={datalistId}>
          {fieldSuggestions.map(f => (
            <option key={f.path} value={f.path}>{f.label}</option>
          ))}
        </datalist>
      </div>
      {/* Operator */}
      <select
        value={rule.operator}
        onChange={e => onChange({ ...rule, operator: e.target.value as DecisionOperator })}
        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-1.5 py-1 text-xs text-gray-900 dark:text-gray-100 min-w-[100px]"
        aria-label="Condition operator"
      >
        {CONDITION_OPERATORS.map(op => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </select>
      {/* Value */}
      {needsValue && (
        <input
          type="text"
          value={rule.value || ''}
          onChange={e => onChange({ ...rule, value: e.target.value })}
          placeholder={rule.operator === 'in' || rule.operator === 'notIn' ? 'val1, val2, ...' : 'Value'}
          className="flex-1 min-w-0 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
          aria-label="Condition value"
        />
      )}
      {/* Value2 (between) */}
      {needsValue2 && (
        <input
          type="text"
          value={rule.value2 || ''}
          onChange={e => onChange({ ...rule, value2: e.target.value })}
          placeholder="Upper"
          className="w-16 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
          aria-label="Upper bound value"
        />
      )}
      {/* Remove */}
      <button
        type="button"
        onClick={onRemove}
        disabled={!canRemove}
        className="p-1 text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
        aria-label="Remove condition"
      >
        ×
      </button>
    </div>
  );
}

// --- Condition Group Editor (recursive) ---
function ConditionGroupEditor({
  group,
  fieldSuggestions,
  onChange,
  onRemove,
  depth = 0,
}: {
  group: ConditionGroup;
  fieldSuggestions: FieldSuggestion[];
  onChange: (updated: ConditionGroup) => void;
  onRemove?: () => void;
  depth?: number;
}) {
  const updateRule = (idx: number, rule: ConditionRule) => {
    const updated = [...group.conditions];
    updated[idx] = rule;
    onChange({ ...group, conditions: updated });
  };
  const removeRule = (idx: number) => {
    const updated = group.conditions.filter((_, i) => i !== idx);
    onChange({ ...group, conditions: updated });
  };
  const addRule = () => {
    onChange({ ...group, conditions: [...group.conditions, createEmptyRule()] });
  };
  const updateSubGroup = (idx: number, subGroup: ConditionGroup) => {
    const updated = [...group.groups];
    updated[idx] = subGroup;
    onChange({ ...group, groups: updated });
  };
  const removeSubGroup = (idx: number) => {
    const updated = group.groups.filter((_, i) => i !== idx);
    onChange({ ...group, groups: updated });
  };
  const addSubGroup = () => {
    onChange({ ...group, groups: [...group.groups, createEmptyGroup(group.logic === 'AND' ? 'OR' : 'AND')] });
  };

  return (
    <div className={`rounded-lg border ${depth === 0 ? 'border-gray-200 dark:border-gray-600' : 'border-gray-300 dark:border-gray-500 border-dashed'} p-2.5 ${depth > 0 ? 'bg-gray-50/50 dark:bg-gray-700/30' : ''}`}>
      {/* Logic toggle + header */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Match</span>
        <button
          type="button"
          onClick={() => onChange({ ...group, logic: 'AND' })}
          className={`px-2 py-0.5 text-[10px] font-bold rounded ${group.logic === 'AND' ? 'bg-primary-700 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}`}
        >
          ALL
        </button>
        <button
          type="button"
          onClick={() => onChange({ ...group, logic: 'OR' })}
          className={`px-2 py-0.5 text-[10px] font-bold rounded ${group.logic === 'OR' ? 'bg-primary-700 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}`}
        >
          ANY
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="ml-auto p-1 text-xs text-red-400 hover:text-red-600"
            aria-label="Remove group"
          >
            Remove Group
          </button>
        )}
      </div>

      {/* Rules */}
      <div className="space-y-1.5">
        {group.conditions.map((rule, idx) => (
          <ConditionRuleEditor
            key={idx}
            rule={rule}
            fieldSuggestions={fieldSuggestions}
            onChange={r => updateRule(idx, r)}
            onRemove={() => removeRule(idx)}
            canRemove={group.conditions.length > 1 || group.groups.length > 0}
          />
        ))}
      </div>

      {/* Sub-groups */}
      {group.groups.length > 0 && (
        <div className="mt-2 space-y-2">
          {group.groups.map((subGroup, idx) => (
            <ConditionGroupEditor
              key={idx}
              group={subGroup}
              fieldSuggestions={fieldSuggestions}
              onChange={g => updateSubGroup(idx, g)}
              onRemove={() => removeSubGroup(idx)}
              depth={depth + 1}
            />
          ))}
        </div>
      )}

      {/* Add buttons */}
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          onClick={addRule}
          className="text-[10px] text-primary-700 hover:text-primary-800 font-medium"
        >
          + Add Condition
        </button>
        {depth < 2 && (
          <button
            type="button"
            onClick={addSubGroup}
            className="text-[10px] text-purple-600 hover:text-purple-800 font-medium"
          >
            + Add Group
          </button>
        )}
      </div>
    </div>
  );
}

interface DecisionEditorProps {
  node: DecisionNode;
  edges: Edge[];
  allNodes: Node[];
  endpointType: string | number;
  onSave: (updates: {
    conditionField: string;
    conditionOperator: string;
    label: string;
    edges: Array<{ id: string; label: string; conditionValue: string; isDefault: boolean; sortOrder: number }>;
  }) => void;
  onClose: () => void;
}

function DecisionNodeEditor({ node, edges, allNodes, endpointType, onSave, onClose }: DecisionEditorProps) {
  const outgoingEdges = edges.filter(e => e.source === node.id);
  const [label, setLabel] = useState(node.data.label || 'Decision');
  const fieldSuggestions = useMemo(
    () => getAvailableConditionFields(node.id, allNodes, edges, endpointType),
    [node.id, allNodes, edges, endpointType],
  );

  const [branchEdges, setBranchEdges] = useState(() => {
    const raw = outgoingEdges.map((e, i) => {
      const edgeData = e.data as Record<string, unknown> | undefined;
      const conditionValue = edgeData?.conditionValue as string | undefined;
      return {
        id: e.id,
        label: (edgeData?.label as string) || '',
        conditionGroup: parseEdgeConditionGroup(conditionValue),
        isDefault: (edgeData?.isDefault as boolean) || false,
        sortOrder: i,
        targetName: String(allNodes.find(n => n.id === e.target)?.data?.label || 'Unknown'),
        targetId: e.target,
      };
    });
    // Disambiguate when multiple branches target nodes with the same name
    const nameCount = new Map<string, number>();
    for (const b of raw) nameCount.set(b.targetName, (nameCount.get(b.targetName) || 0) + 1);
    const nameIdx = new Map<string, number>();
    for (const b of raw) {
      if ((nameCount.get(b.targetName) || 0) > 1) {
        const idx = (nameIdx.get(b.targetName) || 0) + 1;
        nameIdx.set(b.targetName, idx);
        b.targetName = `${b.targetName} (#${idx})`;
      }
    }
    return raw;
  });

  const handleSave = () => {
    onSave({
      conditionField: '', // No longer used at node level
      conditionOperator: 'equals', // Legacy compat
      label,
      edges: branchEdges.map(b => ({
        id: b.id,
        label: b.label,
        conditionValue: b.isDefault ? '' : JSON.stringify(b.conditionGroup),
        isDefault: b.isDefault,
        sortOrder: b.sortOrder,
      })),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Edit decision node"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[85vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Edit Decision Node
        </h3>

        {/* Node name */}
        <div className="mb-4">
          <label htmlFor="decision-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name
          </label>
          <input
            id="decision-name"
            type="text"
            value={label}
            onChange={e => setLabel(e.target.value)}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-700 focus:border-transparent"
          />
        </div>

        {/* Branch conditions */}
        {branchEdges.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Branch Conditions
            </h4>
            <div className="space-y-4">
              {branchEdges.map((branch, idx) => (
                <div key={branch.id} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                      {branch.label ? `${branch.label} →` : `Branch ${idx + 1} →`} {branch.targetName}
                    </span>
                    <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 ml-auto cursor-pointer">
                      <input
                        type="checkbox"
                        checked={branch.isDefault}
                        onChange={e => {
                          setBranchEdges(prev => prev.map((b, i) => ({
                            ...b,
                            isDefault: i === idx ? e.target.checked : (e.target.checked ? false : b.isDefault),
                          })));
                        }}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                      Default (else)
                    </label>
                  </div>
                  {!branch.isDefault && (
                    <>
                      {/* Label */}
                      <div className="mb-2">
                        <input
                          type="text"
                          placeholder="Branch label (e.g., High Severity)"
                          value={branch.label}
                          onChange={e => setBranchEdges(prev => prev.map((b, i) => i === idx ? { ...b, label: e.target.value } : b))}
                          className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
                          aria-label={`Branch label for ${branch.targetName}`}
                        />
                      </div>
                      {/* Condition group builder */}
                      <ConditionGroupEditor
                        group={branch.conditionGroup}
                        fieldSuggestions={fieldSuggestions}
                        onChange={g => setBranchEdges(prev => prev.map((b, i) => i === idx ? { ...b, conditionGroup: g } : b))}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {branchEdges.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Connect outgoing edges from this node to define branches. Drag from the bottom handle to another node.
          </p>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-lg hover:bg-primary-800"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Editor ---

const nodeTypes: NodeTypes = {
  step: StepNodeComponent,
  decision: DecisionNodeComponent,
  merge: MergeNodeComponent,
};

const edgeTypes: EdgeTypes = {
  conditional: ConditionalEdge,
};

function graphToFlowNodes(graph: PipelineGraphDto): Node[] {
  return graph.nodes.map((n): Node => {
    const base = {
      id: n.id,
      position: { x: n.positionX, y: n.positionY },
    };

    switch (n.nodeType) {
      case 'Decision':
        return {
          ...base,
          type: 'decision' as const,
          data: {
            label: n.name,
            conditionField: n.conditionField || '',
            conditionOperator: n.conditionOperator || 'equals',
            branchSubtitle: undefined,
            nodeType: 'Decision' as const,
          },
        };
      case 'Merge':
        return {
          ...base,
          type: 'merge' as const,
          data: {
            label: n.name,
            nodeType: 'Merge' as const,
          },
        };
      default:
        return {
          ...base,
          type: 'step' as const,
          data: {
            label: n.name,
            stepType: n.stepType,
            isEnabled: getStepEnabledState(n.isEnabled, n.configuration),
            configuration: n.configuration,
            nodeType: 'Step' as const,
            icon: stepIcons[n.stepType || ''] || '⚙️',
          },
        };
    }
  });
}

function graphToFlowEdges(graph: PipelineGraphDto): Edge[] {
  return graph.edges.map(e => ({
    id: e.id,
    source: e.sourceNodeId,
    target: e.targetNodeId,
    type: 'conditional',
    markerEnd: { type: MarkerType.ArrowClosed },
    data: {
      label: e.label || '',
      conditionValue: e.conditionValue || '',
      isDefault: e.isDefault,
      sortOrder: e.sortOrder,
    },
  }));
}

function getEdgeLabelForBranchContext(edge: Edge): string | undefined {
  const edgeData = edge.data as Record<string, unknown> | undefined;
  if (edgeData?.isDefault === true) {
    return 'Default';
  }
  const label = (edgeData?.label as string | undefined)?.trim();
  if (label) {
    return label;
  }
  const conditionValue = (edgeData?.conditionValue as string | undefined)?.trim();
  if (conditionValue) {
    return conditionValue;
  }
  return undefined;
}

function applyBranchSubtitles(nodes: Node[], edges: Edge[]): Node[] {
  const nodesById = new Map(nodes.map(node => [node.id, node]));
  const edgesBySource = new Map<string, Edge[]>();
  const incomingCount = new Map<string, number>();

  for (const edge of edges) {
    const bySource = edgesBySource.get(edge.source) ?? [];
    bySource.push(edge);
    edgesBySource.set(edge.source, bySource);
    incomingCount.set(edge.target, (incomingCount.get(edge.target) ?? 0) + 1);
  }

  for (const sourceEdges of edgesBySource.values()) {
    sourceEdges.sort((a, b) => {
      const sortOrderA = ((a.data as Record<string, unknown> | undefined)?.sortOrder as number | undefined) ?? 0;
      const sortOrderB = ((b.data as Record<string, unknown> | undefined)?.sortOrder as number | undefined) ?? 0;
      return sortOrderA - sortOrderB;
    });
  }

  const startNodes = nodes.filter(node => (incomingCount.get(node.id) ?? 0) === 0);
  const initialNodes = startNodes.length > 0 ? startNodes : [...nodes].sort((a, b) => a.position.y - b.position.y);
  const queue: Array<{ nodeId: string; branchContext?: string }> = initialNodes.map(node => ({ nodeId: node.id }));
  const visited = new Set<string>();
  const branchContextByNode = new Map<string, string | undefined>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || visited.has(current.nodeId)) {
      continue;
    }

    visited.add(current.nodeId);
    branchContextByNode.set(current.nodeId, current.branchContext);

    const node = nodesById.get(current.nodeId);
    if (!node) {
      continue;
    }

    const nodeData = node.data as FlowNodeData;
    const outEdges = edgesBySource.get(current.nodeId) ?? [];

    for (const edge of outEdges) {
      let nextBranchContext = current.branchContext;

      if (nodeData.nodeType === 'Decision') {
        nextBranchContext = getEdgeLabelForBranchContext(edge);
      } else if (nodeData.nodeType === 'Merge') {
        nextBranchContext = undefined;
      }

      queue.push({ nodeId: edge.target, branchContext: nextBranchContext });
    }
  }

  return nodes.map(node => {
    const data = node.data as FlowNodeData;
    if (data.nodeType !== 'Step' && data.nodeType !== 'Decision') {
      return node;
    }

    return {
      ...node,
      data: {
        ...data,
        branchSubtitle: branchContextByNode.get(node.id),
      },
    };
  });
}

function flowNodesToDto(nodes: Node[]): PipelineNodeDto[] {
  return nodes.map(n => {
    const d = n.data as FlowNodeData;
    return {
      id: n.id,
      nodeType: d.nodeType,
      stepType: d.nodeType === 'Step' ? ((d as StepNodeData).stepType as PipelineNodeDto['stepType']) : undefined,
      name: d.label,
      positionX: Math.round(n.position.x),
      positionY: Math.round(n.position.y),
      configuration: d.nodeType === 'Step' ? ((d as StepNodeData).configuration ?? undefined) : undefined,
      isEnabled: d.nodeType === 'Step' ? (d as StepNodeData).isEnabled : true,
      conditionField: d.nodeType === 'Decision' ? (d as DecisionNodeData).conditionField || undefined : undefined,
      conditionOperator: d.nodeType === 'Decision' ? (d as DecisionNodeData).conditionOperator || undefined : undefined,
    };
  });
}

function flowEdgesToDto(edges: Edge[]): PipelineEdgeDto[] {
  return edges.map((e, i) => ({
    id: e.id,
    sourceNodeId: e.source,
    targetNodeId: e.target,
    label: (e.data as Record<string, unknown>)?.label as string || undefined,
    conditionValue: (e.data as Record<string, unknown>)?.conditionValue as string || undefined,
    sortOrder: (e.data as Record<string, unknown>)?.sortOrder as number ?? i,
    isDefault: (e.data as Record<string, unknown>)?.isDefault as boolean || false,
  }));
}

type DecisionNode = Node<DecisionNodeData, 'decision'>;

export function PipelineFlowEditor({ graph, onSave, endpointType, tenantId, customerId }: PipelineFlowEditorProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState(graphToFlowNodes(graph));
  const [edges, setEdges, onEdgesChange] = useEdgesState(graphToFlowEdges(graph));
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [editingDecision, setEditingDecision] = useState<DecisionNode | null>(null);
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const rfInstance = useRef<ReactFlowInstance<Node, Edge> | null>(null);
  const nodesForRender = useMemo(() => applyBranchSubtitles(nodes, edges), [nodes, edges]);
  const edgesForRender = useMemo(() => {
    const outgoingCountBySource = new Map<string, number>();
    for (const edge of edges) {
      outgoingCountBySource.set(edge.source, (outgoingCountBySource.get(edge.source) ?? 0) + 1);
    }

    return edges.map(edge => ({
      ...edge,
      data: {
        ...(edge.data as Record<string, unknown> | undefined),
        showLabel: (outgoingCountBySource.get(edge.source) ?? 0) > 1,
      },
    }));
  }, [edges]);

  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    onNodesChange(changes);
    setIsDirty(true);
  }, [onNodesChange]);

  const handleEdgesChange = useCallback((changes: Parameters<typeof onEdgesChange>[0]) => {
    onEdgesChange(changes);
    setIsDirty(true);
  }, [onEdgesChange]);

  const onConnect: OnConnect = useCallback((connection: Connection) => {
    const newEdge: Edge = {
      ...connection,
      id: crypto.randomUUID(),
      type: 'conditional',
      markerEnd: { type: MarkerType.ArrowClosed },
      data: { label: '', conditionValue: '', isDefault: false, sortOrder: 0 },
    };
    setEdges(eds => addEdge(newEdge, eds));
    setIsDirty(true);
  }, [setEdges]);

  const handleNodeDoubleClick = useCallback((_: React.MouseEvent, node: Node) => {
    const data = node.data as FlowNodeData;
    if (data.nodeType === 'Decision') {
      setEditingStepId(null);
      setEditingDecision(node as DecisionNode);
    } else if (data.nodeType === 'Step') {
      setEditingDecision(null);
      setEditingStepId(node.id);
    }
  }, []);

  const handleEdgeDoubleClick = useCallback((_: React.MouseEvent, edge: Edge) => {
    const sourceNode = nodes.find(n => n.id === edge.source);
    if ((sourceNode?.data as FlowNodeData)?.nodeType === 'Decision') {
      setEditingDecision(sourceNode as DecisionNode);
    }
  }, [nodes]);

  const addNode = useCallback((type: 'Decision' | 'Merge' | 'Step', stepType?: string) => {
    const id = crypto.randomUUID();
    // Place new nodes at the center of the visible viewport
    const wrapper = reactFlowWrapper.current;
    const wrapperWidth = wrapper?.clientWidth || 800;
    const wrapperHeight = wrapper?.clientHeight || 600;
    let centerX = 400;
    let centerY = 300;
    if (rfInstance.current) {
      // Use screenToFlowPosition for accurate placement regardless of pan/zoom
      const pos = rfInstance.current.screenToFlowPosition({
        x: (wrapper?.getBoundingClientRect().left || 0) + wrapperWidth / 2,
        y: (wrapper?.getBoundingClientRect().top || 0) + wrapperHeight / 2,
      });
      centerX = pos.x;
      centerY = pos.y;
    }

    let newNode: Node;
    if (type === 'Decision') {
      newNode = {
        id,
        type: 'decision',
        position: { x: centerX, y: centerY },
        data: { label: 'New Decision', conditionField: '', conditionOperator: 'equals', nodeType: 'Decision' },
      };
    } else if (type === 'Merge') {
      newNode = {
        id,
        type: 'merge',
        position: { x: centerX, y: centerY },
        data: { label: 'Merge', nodeType: 'Merge' },
      };
    } else {
      newNode = {
        id,
        type: 'step',
        position: { x: centerX, y: centerY },
        data: {
          label: stepType || 'New Step',
          stepType: stepType || null,
          isEnabled: true,
          configuration: null,
          nodeType: 'Step',
          icon: stepIcons[stepType || ''] || '⚙️',
        },
      };
    }

    setNodes(nds => [...nds, newNode]);
    setIsDirty(true);
  }, [setNodes]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave(flowNodesToDto(nodes), flowEdgesToDto(edges));
      setIsDirty(false);
    } catch {
      // Error handling by parent
    } finally {
      setIsSaving(false);
    }
  }, [nodes, edges, onSave]);

  const handleDecisionEditorSave = useCallback((updates: {
    conditionField: string;
    conditionOperator: string;
    label: string;
    edges: Array<{ id: string; label: string; conditionValue: string; isDefault: boolean; sortOrder: number }>;
  }) => {
    if (!editingDecision) return;

    // Update the node data
    setNodes(nds => nds.map(n => {
      if (n.id !== editingDecision.id) return n;
      return {
        ...n,
        data: {
          ...n.data,
          label: updates.label,
          conditionField: updates.conditionField,
          conditionOperator: updates.conditionOperator,
        },
      };
    }));

    // Update edge data
    setEdges(eds => eds.map(e => {
      const edgeUpdate = updates.edges.find(ue => ue.id === e.id);
      if (!edgeUpdate) return e;
      return {
        ...e,
        data: {
          ...e.data as Record<string, unknown>,
          label: edgeUpdate.label,
          conditionValue: edgeUpdate.conditionValue,
          isDefault: edgeUpdate.isDefault,
          sortOrder: edgeUpdate.sortOrder,
        },
      };
    }));

    setEditingDecision(null);
    setIsDirty(true);
  }, [editingDecision, setNodes, setEdges]);

  // Handle step config panel save
  const handleStepConfigSave = useCallback((nodeId: string, configuration: string, isEnabled: boolean) => {
    setNodes(nds => nds.map(n => {
      if (n.id !== nodeId) return n;
      return {
        ...n,
        data: { ...n.data, configuration, isEnabled },
        style: isEnabled ? undefined : { opacity: 0.5 },
      };
    }));
    setEditingStepId(null);
    setIsDirty(true);
  }, [setNodes]);

  // Find the currently editing step node
  const editingStepNode = useMemo(() => {
    if (!editingStepId) return null;
    return nodes.find(n => n.id === editingStepId) ?? null;
  }, [editingStepId, nodes]);

  const [showAddMenu, setShowAddMenu] = useState(false);

  useEscapeKey(() => setEditingDecision(null), !!editingDecision);
  useEscapeKey(() => setShowAddMenu(false), !editingDecision && !editingStepId && showAddMenu);

  // Memoize default viewport
  const defaultViewport = useMemo(() => ({ x: 50, y: 50, zoom: 0.85 }), []);

  return (
    <div className="relative h-full" ref={reactFlowWrapper}>
      <div className="h-full border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-gray-50 dark:bg-gray-900">
        <ReactFlow<Node, Edge>
          nodes={nodesForRender}
          edges={edgesForRender}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={onConnect}
          onNodeDoubleClick={handleNodeDoubleClick}
          onEdgeDoubleClick={handleEdgeDoubleClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultViewport={defaultViewport}
          onInit={(instance) => { rfInstance.current = instance; }}
          fitView
          deleteKeyCode={['Backspace', 'Delete']}
          selectionOnDrag
          selectionMode={SelectionMode.Partial}
          panOnDrag={[1, 2]}
          snapToGrid
          snapGrid={[20, 20]}
          className="bg-gray-50 dark:bg-gray-900"
        >
          <Background gap={20} size={1} />
          <Controls />
          <MiniMap
            nodeStrokeWidth={3}
            className="!bg-gray-100 dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700"
          />

          {/* SVG marker definitions */}
          <svg style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0 }}>
            <defs>
              <marker id="arrow-amber" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#f59e0b" />
              </marker>
              <marker id="arrow-gray" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#9ca3af" />
              </marker>
            </defs>
          </svg>

          {/* Toolbar */}
          <Panel position="top-left" className="flex gap-2 flex-wrap">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAddMenu(prev => !prev)}
                className="px-3 py-2 text-xs font-medium text-white bg-primary-700 rounded-lg hover:bg-primary-800 shadow-sm flex items-center gap-1"
                aria-label="Add node to pipeline"
                aria-expanded={showAddMenu}
              >
                <span className="text-base leading-none">+</span> Add Node
              </button>
              {showAddMenu && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 z-50 py-1">
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Control Nodes
                  </div>
                  <button
                    type="button"
                    onClick={() => { addNode('Decision'); setShowAddMenu(false); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-amber-900/20 flex items-center gap-2"
                  >
                    <span className="text-amber-500">◆</span> Decision (Branch)
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Processing Steps
                  </div>
                  {STEP_TYPES.map(st => (
                    <button
                      key={st}
                      type="button"
                      onClick={() => { addNode('Step', st); setShowAddMenu(false); }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-2"
                    >
                      <span>{stepIcons[st]}</span> {st}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Panel>

          {/* Save / Status bar */}
          <Panel position="top-right" className="flex gap-2 items-center">
            {isDirty && (
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Unsaved changes</span>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              className={`px-4 py-2 text-xs font-medium rounded-lg shadow-sm ${
                isDirty
                  ? 'text-white bg-green-700 hover:bg-green-800'
                  : 'text-gray-500 bg-gray-200 dark:bg-gray-700 cursor-not-allowed'
              }`}
              aria-label="Save pipeline graph"
            >
              {isSaving ? 'Saving...' : 'Save Graph'}
            </button>
          </Panel>

          {/* Instructions panel */}
          <Panel position="bottom-center" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
            <span className="font-medium">Tips:</span>{' '}
            Drag to select multiple • Middle/right-click drag to pan • Drag between handles to connect • Double-click to edit • Delete/Backspace to remove selected
          </Panel>
        </ReactFlow>
      </div>

      {/* Decision node editor modal */}
      {editingDecision && (
        <DecisionNodeEditor
          node={editingDecision}
          edges={edges}
          allNodes={nodes}
          endpointType={endpointType || ''}
          onSave={handleDecisionEditorSave}
          onClose={() => setEditingDecision(null)}
        />
      )}

      {/* Step config panel (slide-over) */}
      {editingStepNode && (
        <StepConfigPanel
          nodeId={editingStepNode.id}
          stepType={(editingStepNode.data as { stepType: string | null }).stepType || ''}
          label={(editingStepNode.data as { label: string }).label}
          isEnabled={(editingStepNode.data as { isEnabled?: boolean }).isEnabled !== false}
          configuration={(editingStepNode.data as { configuration: string | null }).configuration}
          endpointType={endpointType || ''}
          tenantId={tenantId}
          customerId={customerId}
          nodes={nodes}
          edges={edges}
          onSave={handleStepConfigSave}
          onClose={() => setEditingStepId(null)}
        />
      )}
    </div>
  );
}
